import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Typography } from '@mui/material';
import { getCustomerPortalTenants } from '../../api/customerPortalApi';
import { getCustomerPortalTenantAccessStatus } from '../../api/tenantAccessApi';
import { getCustomerPortalTenantSetupContext } from '../../api/setupWizardApi';
import { getPortalTenantProvisioningStatus } from '../../api/tenantProvisioningApi';
import { getPortalTenantSchemaStatus } from '../../api/tenantSchemaApi';
import { colorTokens, typographyTokens } from '../../app/designTokens';
import { DataTable } from '../../components/DataTable';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { TableToolbar } from '../../components/TableToolbar';
import { Button } from '../../components/ui/Button';
import { portalSchemaMessage } from '../../components/TenantSchemaTab';
import { portalProvisioningMessage } from '../../components/TenantProvisioningTab';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';
import {
  formatAccessGateStatus,
  formatApplicationReadiness,
  formatProvisioningStatus,
  formatSchemaStatus,
  formatSetupStatus,
  formatTenantStatus,
} from '../../utils/customerPortalLabels';

const { semantic } = colorTokens;

/** Portal tenants list may breathe wider than the default 1008 rail. */
const PORTAL_TENANTS_CONTENT_MAX_WIDTH = 1136;

const badgeSx = {
  alignSelf: 'flex-start',
  flexShrink: 0,
};

const helperSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 12,
  lineHeight: '16px',
  letterSpacing: '0.02em',
  color: semantic.text.muted,
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  minWidth: 0,
  maxWidth: 140,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const helperDangerSx = {
  ...helperSx,
  color: semantic.error,
};

const monoSx = {
  fontFamily: typographyTokens.fontFamilyMono,
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.secondary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  minWidth: 0,
  maxWidth: '100%',
};

const emptyMutedSx = {
  color: semantic.text.muted,
  fontSize: 13,
  lineHeight: '18px',
};

const nameSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '20px',
  fontWeight: typographyTokens.fontWeightSemibold,
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  minWidth: 0,
};

const cellStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  minWidth: 0,
  maxWidth: '100%',
};

function launchStatusPath(value) {
  if (typeof value !== 'string') {
    return null;
  }
  if (!/^\/register\/status\/[A-Za-z0-9_-]+$/.test(value)) {
    return null;
  }
  return value;
}

function TenantStatusCell({ badge, helper, helperSx: helperStyle = helperSx }) {
  return (
    <Box sx={cellStackSx}>
      {badge}
      {helper ? (
        <Typography component="span" sx={helperStyle}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

export function CustomerPortalTenantsPage() {
  const { t } = useTranslation([
    'tenants',
    'common',
    'statuses',
    'setupWizard',
    'tenantProvisioning',
    'tenantSchema',
    'tenantSchemaRunner',
    'customerPortal',
  ]);
  const { activeCustomerId, loading: portalLoading } = useCustomerPortal();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const emptyValue = t('common:empty_value');
  const recordCount = tenants.length;

  const loadTenants = useCallback(() => {
    if (!activeCustomerId) {
      return;
    }

    setLoading(true);
    setError('');

    getCustomerPortalTenants(activeCustomerId)
      .then(async (response) => {
        const items = response.data || [];
        const enriched = await Promise.all(items.map(async (tenant) => {
          try {
            const [access, setup, provisioning, schema] = await Promise.all([
              getCustomerPortalTenantAccessStatus(activeCustomerId, tenant.id),
              getCustomerPortalTenantSetupContext(activeCustomerId, tenant.id).catch(() => null),
              getPortalTenantProvisioningStatus(activeCustomerId, tenant.id).catch(() => null),
              getPortalTenantSchemaStatus(activeCustomerId, tenant.id).catch(() => null),
            ]);
            return {
              ...tenant,
              access_gate: access.data || access,
              setup_context: setup?.data || setup,
              provisioning_status: provisioning?.data || provisioning,
              schema_status: schema?.data || schema,
            };
          } catch {
            return tenant;
          }
        }));
        setTenants(enriched);
      })
      .catch(() => setError(t('tenants:portal.load_failed')))
      .finally(() => setLoading(false));
  }, [activeCustomerId, t]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const columns = useMemo(() => [
    {
      id: 'name',
      label: t('common:name'),
      width: '18%',
      render: (row) => (
        <Typography component="span" sx={nameSx}>
          {row.name || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      width: '8%',
      render: (row) => (
        row.status
          ? (
            <TenantStatusCell
              badge={(
                <StatusBadge
                  status={row.status}
                  category="tenant"
                  apiLabel={formatTenantStatus(row.status)}
                  size="Sm"
                  sx={badgeSx}
                />
              )}
            />
          )
          : <Typography component="span" sx={emptyMutedSx}>{emptyValue}</Typography>
      ),
    },
    {
      id: 'application_readiness',
      label: t('tenants:portal.application_readiness'),
      width: '14%',
      render: (row) => {
        const presentation = row.application_readiness?.presentation;
        const statusPath = launchStatusPath(row.application_readiness?.status_path);
        const hostAllowed = row.application_readiness?.customer_host_allowed;
        const showLegacyReadiness = !presentation || presentation === 'legacy';

        if (showLegacyReadiness) {
          return <Typography component="span" sx={emptyMutedSx}>{emptyValue}</Typography>;
        }

        let helper = null;
        let helperStyle = helperSx;
        if (presentation === 'ready' && hostAllowed === false) {
          helper = t('tenants:portal.application_not_currently_accessible');
        } else if (presentation === 'preparing') {
          helper = t('tenants:portal.application_preparing_caption');
        } else if (presentation === 'simulated') {
          helper = t('tenants:portal.application_simulated_caption');
        } else if (presentation === 'failed') {
          helper = t('tenants:portal.application_failed_retry');
          helperStyle = helperDangerSx;
        }

        return (
          <Box sx={cellStackSx}>
            <StatusBadge
              status={presentation}
              category="application_readiness"
              apiLabel={formatApplicationReadiness(presentation)}
              size="Sm"
              sx={badgeSx}
            />
            {helper ? (
              <Typography component="span" sx={helperStyle}>
                {helper}
              </Typography>
            ) : null}
            {presentation === 'failed' && statusPath ? (
              <Typography
                component={RouterLink}
                to={statusPath}
                sx={{ ...helperSx, color: semantic.text.secondary, textDecoration: 'underline' }}
              >
                {t('tenants:portal.application_failed_status_link')}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      id: 'domain',
      label: t('common:domain'),
      width: '11%',
      render: (row) => (
        row.primary_domain
          ? <Typography sx={monoSx}>{row.primary_domain}</Typography>
          : <Typography component="span" sx={emptyMutedSx}>{emptyValue}</Typography>
      ),
    },
    {
      id: 'access_gate',
      label: t('tenants:portal.access_gate'),
      width: '15%',
      render: (row) => {
        if (row.access_gate) {
          const accessStatus = row.access_gate.access_status || 'unknown';
          const helper = !row.access_gate.can_login
            ? (
              row.access_gate.requires_payment_action
                ? t('tenants:portal.payment_required')
                : t('customerPortal:safeMessages.accountNeedsAttentionBeforeLaunch')
            )
            : null;

          return (
            <TenantStatusCell
              badge={(
                <StatusBadge
                  status={accessStatus}
                  category="tenant_access"
                  apiLabel={formatAccessGateStatus(accessStatus)}
                  size="Sm"
                  sx={badgeSx}
                />
              )}
              helper={helper}
              helperSx={helperDangerSx}
            />
          );
        }

        const hostAllowed = row.application_readiness?.customer_host_allowed;
        const allowed = row.application_readiness?.kind === 'canonical'
          ? hostAllowed === true
          : row.is_access_allowed;
        const fallbackStatus = allowed ? 'allowed' : 'blocked';
        return (
          <TenantStatusCell
            badge={(
              <StatusBadge
                status={fallbackStatus}
                category="tenant_access"
                apiLabel={allowed
                  ? t('statuses:access.allowed')
                  : t('statuses:access.blocked')}
                size="Sm"
                sx={badgeSx}
              />
            )}
          />
        );
      },
    },
    {
      id: 'setup',
      label: t('tenants:portal.setup_status'),
      width: '11%',
      render: (row) => {
        const setupStatus = row.setup_context?.setup_status;
        if (!setupStatus) {
          return <Typography component="span" sx={emptyMutedSx}>{emptyValue}</Typography>;
        }

        return (
          <TenantStatusCell
            badge={(
              <StatusBadge
                status={setupStatus}
                category="setup_session"
                apiLabel={formatSetupStatus(setupStatus)}
                size="Sm"
                sx={badgeSx}
              />
            )}
            helper={`${Math.round(row.setup_context.progress_percent ?? 0)}%`}
          />
        );
      },
    },
    {
      id: 'provisioning',
      label: t('tenants:portal.provisioning_status'),
      width: '12%',
      render: (row) => {
        const provisioningStatus = row.provisioning_status?.provisioning_status;
        if (!provisioningStatus) {
          return <Typography component="span" sx={emptyMutedSx}>{emptyValue}</Typography>;
        }

        return (
          <TenantStatusCell
            badge={(
              <StatusBadge
                status={provisioningStatus}
                category="provisioning_status"
                apiLabel={formatProvisioningStatus(provisioningStatus)}
                size="Sm"
                sx={badgeSx}
              />
            )}
            helper={portalProvisioningMessage(
              t,
              row.provisioning_status.message_key || row.provisioning_status.message,
            )}
          />
        );
      },
    },
    {
      id: 'schema',
      label: t('tenantSchema:portal.schema_status'),
      width: '11%',
      render: (row) => {
        const schemaStatus = row.schema_status?.schema_status;
        if (!schemaStatus) {
          return <Typography component="span" sx={emptyMutedSx}>{emptyValue}</Typography>;
        }

        return (
          <TenantStatusCell
            badge={(
              <StatusBadge
                status={schemaStatus}
                category="schema_plan"
                apiLabel={formatSchemaStatus(schemaStatus)}
                size="Sm"
                sx={badgeSx}
              />
            )}
            helper={portalSchemaMessage(
              t,
              row.schema_status.message_key || row.schema_status.message,
            )}
          />
        );
      },
    },
  ], [emptyValue, t]);

  if (portalLoading) {
    return <LoadingSkeleton type="Detail" />;
  }

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        width: { xs: '100%', md: PORTAL_TENANTS_CONTENT_MAX_WIDTH },
        maxWidth: {
          xs: '100%',
          md: `min(${PORTAL_TENANTS_CONTENT_MAX_WIDTH}px, calc(100% + 192px))`,
        },
        position: { md: 'relative' },
        left: { md: '50%' },
        transform: { md: 'translateX(-50%)' },
      }}
    >
      <PageHeader
        type="List"
        title={t('tenants:portal.title')}
        subtitle={t('tenants:portal.list_subtitle')}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadTenants} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('tenants:portal.title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('tenants:portal.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton type="Table" embedded rows={4} columns={columns.length} />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={tenants}
            getRowKey={(row) => row.id}
            emptyMessage={t('tenants:portal.empty_list')}
            sx={{
              overflowX: 'hidden',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: 0,
                tableLayout: 'fixed',
              },
              '& .MuiTableCell-root': {
                verticalAlign: 'top',
                overflow: 'hidden',
                py: 2,
              },
              '& .MuiTableCell-body': {
                fontSize: 13,
                lineHeight: '20px',
              },
              '& .MuiTableCell-head': {
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                py: 1.5,
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
